import {FC, KeyboardEvent, ReactNode} from 'react';
import {Box, Typography} from '@mui/material';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import './IssueCodingJobResultMetrics.scss';

export type JobResultMetricItem = {
  id: string;
  displayValue: string;
  label: string;
  showInfoTooltip?: boolean;
  tooltip?: ReactNode;
  isClickable?: boolean;
  onClick?: () => void;
  ariaLabel: string;
};

type JobResultMetricsStripProps = {
  metrics: JobResultMetricItem[];
  testId?: string;
  className?: string;
};

const handleMetricKeyDown = (
  event: KeyboardEvent<HTMLDivElement>,
  onClick?: () => void
) => {
  if (event.currentTarget !== event.target || !onClick) {
    return;
  }

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onClick();
  }
};

const JobResultMetricsStrip: FC<JobResultMetricsStripProps> = ({
  metrics,
  testId = 'issue-coding-job-result-metrics',
  className,
}) => (
  <Box
    className={`issue-coding-job-result-metrics${
      className ? ` ${className}` : ''
    }`}
    component="section"
    data-testid={testId}
  >
    {metrics.map((metric) => (
      <Box
        key={metric.id}
        className={`issue-coding-job-result-metrics__cell${
          metric.isClickable
            ? ' issue-coding-job-result-metrics__cell--clickable'
            : ''
        }`}
        tabIndex={metric.isClickable ? 0 : undefined}
        role={metric.isClickable ? 'button' : undefined}
        aria-label={metric.ariaLabel}
        onClick={metric.onClick}
        onKeyDown={(event) => handleMetricKeyDown(event, metric.onClick)}
        data-testid={`${testId}-cell-${metric.id}`}
      >
        <Typography
          className="typo-text subhead4_size issue-coding-job-result-metrics__value"
          component="p"
          aria-hidden="true"
          data-testid={`${testId}-value-${metric.id}`}
        >
          {metric.displayValue}
        </Typography>
        <Typography
          className="typo-text body4_size issue-coding-job-result-metrics__label"
          component="span"
          aria-hidden="true"
          data-testid={`${testId}-label-${metric.id}`}
        >
          <span className="issue-coding-job-result-metrics__label-text">
            {metric.label}
          </span>
          {metric.showInfoTooltip && (
            <span className="issue-coding-job-result-metrics__info">
              <ArrTooltipWrapper
                trigger={
                  <span
                    className="issue-coding-job-result-metrics__info-trigger"
                    data-testid={`${testId}-info-${metric.id}`}
                    tabIndex={0}
                    role="button"
                    aria-label={`More information about ${metric.label}`}
                    onKeyDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <InfoIcon
                      className="issue-coding-job-result-metrics__info-icon"
                      aria-hidden="true"
                      focusable="false"
                    />
                  </span>
                }
                title={metric.tooltip ?? ''}
              >
                {metric.tooltip ?? ''}
              </ArrTooltipWrapper>
            </span>
          )}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default JobResultMetricsStrip;
